import React, { useState } from 'react';
import { Box, ButtonBase, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { colors } from '../../styles/theme';
import { useMetaTooltipProps } from './metaTooltipBoundary';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';

/**
 * Miniatura del anuncio (ajuste 12): cuadrada, `object-fit: cover`, ▶ si es video.
 * Sin `thumbnailUrl` o si la imagen falla (`onError`, la URL de Meta vence): ícono
 * genérico. Con `onClick` es EL botón «Ver anuncio» (no hay otro): `<button>` con
 * `aria-label` «Ver anuncio», Tooltip, cursor pointer, hover con borde teal y foco
 * visible; Enter / Espacio nativos. También con el ícono genérico.
 *
 * @param {{ url?: string|null, isVideo?: boolean, size?: number, radius?: number,
 *   onClick?: (() => void)|null, label?: string, testId?: string }} props
 */
export default function MetaAdThumb({
  url = null, isVideo = false, size = 56, radius = 8, onClick = null, label = 'Ver anuncio', testId,
}) {
  // La URL que falló (si cambia la URL, se vuelve a intentar).
  const [failedUrl, setFailedUrl] = useState(null);
  const tooltipProps = useMetaTooltipProps();
  const showImage = Boolean(url) && failedUrl !== url;
  const play = Math.max(18, Math.round(size * 0.38));
  const content = (
    <>
      {showImage ? (
        <Box
          component="img"
          src={url}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailedUrl(url)}
          data-testid={testId ? `${testId}-img` : undefined}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <ImageOutlinedIcon
          aria-hidden
          data-testid={testId ? `${testId}-generic` : undefined}
          sx={{ fontSize: Math.round(size * 0.42), color: 'rgba(255,255,255,0.45)' }}
        />
      )}
      {isVideo ? (
        <Box
          component="span"
          aria-hidden
          data-testid={testId ? `${testId}-play` : undefined}
          sx={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: play, height: play, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.55)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <PlayArrowRoundedIcon sx={{ fontSize: Math.round(play * 0.75) }} />
        </Box>
      ) : null}
    </>
  );
  const sx = {
    position: 'relative', width: size, height: size, flexShrink: 0, borderRadius: `${radius}px`, overflow: 'hidden',
    bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  if (!onClick) {
    return <Box sx={sx} data-testid={testId} role="img" aria-label={label}>{content}</Box>;
  }
  return (
    <Tooltip title={<span>Ver anuncio</span>} placement="top" arrow describeChild {...tooltipProps}>
      <ButtonBase
        onClick={onClick}
        aria-label={label}
        data-testid={testId}
        focusRipple
        sx={{
          ...sx,
          cursor: 'pointer',
          transition: 'border-color 120ms, box-shadow 120ms, filter 120ms',
          '&:hover': { borderColor: colors.primaryLight, boxShadow: `0 0 0 1px ${alpha(colors.primaryLight, 0.35)}`, filter: 'brightness(1.08)' },
          '&.Mui-focusVisible': { borderColor: colors.primaryLight, outline: `2px solid ${colors.primaryLight}`, outlineOffset: 2 },
        }}
      >
        {content}
      </ButtonBase>
    </Tooltip>
  );
}
